import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  ModalBody,
  Input,
  Label,
  Col,
  Row,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { getCandidateQualifications } from "../../../../actions/customer";
import {
  createContent,
  updateQualification, // 🟢 FIXED: was updateContentsById (wrong endpoint /FormContent/{id})
  deleteQualification,
} from "../../../../api/contents";
import { CustomSnackbar } from "../../../../helpers/CustomSnackbar";
import YearOptions from "../../../../helpers/YearOptions";
import { useSearchParams } from "react-router-dom";
import ConfirmationModal from "../../../../helpers/Common/ConfirmationModal";
import { apiErrorHandler } from "../../../../helpers/common";

const OTHER_OPTION_VALUE = "__OTHER__";

const QualificationModal = ({
  setOpenQualificationModal,
  openQualificationModal,
  modalType = "add",
  qualificationData,
}) => {
  // Safe parsing helper
  const getParsedFields = (fieldData) => {
    if (!fieldData) return {};
    if (typeof fieldData === "object") return fieldData;
    try {
      return JSON.parse(fieldData);
    } catch (e) {
      console.error("Failed to parse additionalFields:", e);
      return {};
    }
  };

  const additional_fields = getParsedFields(
    qualificationData?.additionalFields || qualificationData?.additional_fields,
  );

  const [searchParams] = useSearchParams();
  const isInternal = searchParams.get("isInternal");

  const initialState = {
    title: "",
    university_college: "",
    start_year: "",
    end_year: "",
    details: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [coursesList, setCoursesList] = useState([]);

  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [customCourseName, setCustomCourseName] = useState("");

  const isEditingExisting = modalType === "update" || Boolean(qualificationData?.id);

  const triggerModal = () => setOpenQualificationModal(!openQualificationModal);

  const { profile } = useSelector((state) => state.customer);
  const dispatch = useDispatch();

  const onChangeValue = (e, value) => {
    setFormData({ ...formData, [value]: e.target.value });
  };

  const onCourseSelectChange = (e) => {
    const selectedValue = e.target.value;

    if (selectedValue === OTHER_OPTION_VALUE) {
      setIsOtherSelected(true);
      setFormData((prev) => ({ ...prev, title: "" }));
    } else {
      setIsOtherSelected(false);
      setCustomCourseName("");
      setFormData((prev) => ({ ...prev, title: selectedValue }));
    }
  };

  const onCustomCourseChange = (e) => {
    const value = e.target.value;
    setCustomCourseName(value);
    setFormData((prev) => ({ ...prev, title: value }));
  };

  // 🟢 Fetch courses list from GET API endpoint on mount
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetch(
          "https://sdsjobseekers.runasp.net/api/Qualification/courses",
        );
        if (!response.ok) throw new Error("Failed to fetch courses");
        const result = await response.json();
        const dataArray = Array.isArray(result) ? result : result?.data || [];
        setCoursesList(dataArray);
      } catch (error) {
        console.error("Error fetching courses array:", error);
      }
    };

    fetchCourses();
  }, []);

  useEffect(() => {
    if (isEditingExisting && qualificationData) {
      const existingTitle =
        additional_fields?.title || qualificationData?.course || "";

      const matchesKnownCourse = coursesList.some((course) => {
        const courseName =
          typeof course === "object" ? course.name || course.title : course;
        return courseName === existingTitle;
      });

      if (existingTitle && !matchesKnownCourse) {
        setIsOtherSelected(true);
        setCustomCourseName(existingTitle);
      } else {
        setIsOtherSelected(false);
        setCustomCourseName("");
      }

      setFormData({
        title: existingTitle,
        university_college:
          additional_fields?.university_college ||
          qualificationData?.university ||
          "",
        start_year:
          additional_fields?.start_year || qualificationData?.startYear || "",
        end_year:
          additional_fields?.end_year || qualificationData?.endYear || "",
        details: additional_fields?.details || "",
      });
    } else {
      setFormData(initialState);
      setIsOtherSelected(false);
      setCustomCourseName("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qualificationData, modalType, coursesList]);

  const onQualificationSave = (e) => {
    e.preventDefault();

    setErrors(null);
    setLoading(true);

    const payload = {
      type: "candidate_qualifications",
      name: formData.title,
      candidateId: parseInt(profile?.id),
      course: formData.title,
      university: formData.university_college,
      startYear: parseInt(formData.start_year) || 0,
      endYear: parseInt(formData.end_year) || 0,
      additionalFields: {
        ...formData,
      },
      active: "true",
      displayOrder: 0,
    };

    if (!isEditingExisting) {
      createContent(payload)
        .then((res) => {
          if (res?.status === 200 || res?.status === 201) {
            triggerModal();
            CustomSnackbar.success("Qualification added successfully.");
            setFormData(initialState);
            setIsOtherSelected(false);
            setCustomCourseName("");
            dispatch(getCandidateQualifications(profile.id));
          }
        })
        .catch((err) => {
          apiErrorHandler(err);
        })
        .finally(() => setLoading(false));
    } else {
      // 🟢 FIXED: uses updateQualification -> PUT /Qualification/{id}, matching the correct backend route
      updateQualification(qualificationData?.id, payload)
        .then((res) => {
          if (res?.status === 200 || res?.status === 201) {
            triggerModal();
            CustomSnackbar.success("Qualification updated successfully.");
            setFormData(initialState);
            setIsOtherSelected(false);
            setCustomCourseName("");
            dispatch(getCandidateQualifications(profile.id));
          }
        })
        .catch((err) => {
          apiErrorHandler(err);
        })
        .finally(() => setLoading(false));
    }
  };

  const handleDelete = () => {
    if (!qualificationData?.id) return;

    setDeleteLoading(true);

    deleteQualification(qualificationData.id)
      .then((res) => {
        if (res?.status === 200 || res?.status === 201 || res?.status === 204) {
          triggerModal();
          CustomSnackbar.success("Qualification deleted successfully.");
          dispatch(getCandidateQualifications(profile.id));
        }
      })
      .catch((err) => {
        apiErrorHandler(err);
      })
      .finally(() => {
        setDeleteLoading(false);
        setOpenDeleteModal(false);
      });
  };

  return (
    <>
      <div
        className="modal fade"
        id="applyNow"
        tabIndex="-1"
        aria-labelledby="applyNow"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <Modal isOpen={openQualificationModal} toggle={triggerModal} centered>
            <ModalBody className="modal-body p-4">
              <div className="text-center mb-4">
                <h5 className="modal-title" id="staticBackdropLabel">
                  {isEditingExisting ? "Update" : "Add"} Qualification
                </h5>
              </div>
              <div className="position-absolute end-0 top-0 p-3">
                <button
                  type="button"
                  onClick={triggerModal}
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>

              <form onSubmit={onQualificationSave}>
                <div className="mb-3">
                  <Label for="positionControlInput" className="form-label">
                    Course / Degree *
                  </Label>
                  <Input
                    type="select"
                    className="form-control form-select"
                    id="positionControlInput"
                    value={isOtherSelected ? OTHER_OPTION_VALUE : formData.title}
                    onChange={onCourseSelectChange}
                    invalid={errors?.title ? true : false}
                  >
                    <option value="" disabled>
                      Select your Course name or Degree
                    </option>
                    {coursesList.map((course, index) => {
                      const courseName =
                        typeof course === "object"
                          ? course.name || course.title
                          : course;
                      return (
                        <option key={index} value={courseName}>
                          {courseName}
                        </option>
                      );
                    })}
                    <option value={OTHER_OPTION_VALUE}>Other</option>
                  </Input>
                  <FormFeedback>{errors?.title}</FormFeedback>
                </div>

                {isOtherSelected && (
                  <div className="mb-3">
                    <Label for="customCourseInput" className="form-label">
                      Enter your Course / Degree name *
                    </Label>
                    <Input
                      type="text"
                      className="form-control"
                      id="customCourseInput"
                      placeholder="Type your course or degree name"
                      value={customCourseName}
                      onChange={onCustomCourseChange}
                      invalid={errors?.title ? true : false}
                      autoFocus
                    />
                    <FormFeedback>{errors?.title}</FormFeedback>
                  </div>
                )}

                <div className="mb-3">
                  <Label for="companyControlInput2" className="form-label">
                    University / College *
                  </Label>
                  <Input
                    type="text"
                    className="form-control"
                    id="companyControlInput2"
                    placeholder="Enter your university or college name"
                    value={formData.university_college}
                    onChange={(e) => onChangeValue(e, "university_college")}
                    invalid={errors?.university_college ? true : false}
                  />
                  <FormFeedback>{errors?.university_college}</FormFeedback>
                </div>

                <Row>
                  <Col md={6} className="mb-3">
                    <Label for="choices-year-start" className="form-label">
                      Start year *
                    </Label>
                    <Input
                      type="select"
                      className="form-select"
                      name="choices-year"
                      id="choices-year-start"
                      value={formData?.start_year}
                      onChange={(e) => onChangeValue(e, "start_year")}
                      invalid={errors?.start_year ? true : false}
                    >
                      <YearOptions />
                    </Input>
                    <FormFeedback>{errors?.start_year}</FormFeedback>
                  </Col>

                  <Col md={6} className=" mb-3">
                    <Label for="choices-year-end" className="form-label">
                      End year *
                    </Label>
                    <Input
                      type="select"
                      className="form-select"
                      name="choices-year"
                      id="choices-year-end"
                      value={formData?.end_year}
                      onChange={(e) => onChangeValue(e, "end_year")}
                      invalid={errors?.end_year ? true : false}
                    >
                      <YearOptions />
                    </Input>
                    <FormFeedback>{errors?.end_year}</FormFeedback>
                  </Col>
                </Row>

                <div className="d-flex justify-content-between align-items-center">
                  {isEditingExisting ? (
                    <button
                      type="button"
                      onClick={() => setOpenDeleteModal(true)}
                      className="btn btn-danger"
                    >
                      Delete
                    </button>
                  ) : (
                    <span />
                  )}

                  <div className="text-end">
                    <button
                      type="button"
                      onClick={triggerModal}
                      className="btn btn-secondary me-2"
                    >
                      Cancel
                    </button>

                    {loading ? (
                      <button
                        type="button"
                        className="btn btn-primary disabled"
                      >
                        Save <Spinner size="sm" />
                      </button>
                    ) : (
                      <button type="submit" className="btn btn-primary">
                        Save
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </ModalBody>
          </Modal>
        </div>
      </div>

      <ConfirmationModal
        title="Delete qualification"
        message="Are you sure you want to delete this qualification ?"
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        onSubmit={handleDelete}
        loading={deleteLoading}
      />
    </>
  );
};
export default QualificationModal;